import { EventHandler } from "../../utils/event-handler";
import { EVENT_CLICK } from "../../utils/events";

import "./blocked-content.scss";

declare global {
    interface Window {
        App: Record<string, any>;
        revisitCkyConsent: () => void;
    }
}

interface CookieProvider {
    name: string;
    categories: ('necessary' | 'functional' | 'analytics' | 'performance' | 'advertisement' | 'other')[];
}

export class CookieYes {
    static PROVIDER_YOUTUBE = 'youtube';

    static #providers: CookieProvider[] = [
        {
            name: this.PROVIDER_YOUTUBE,
            categories: [
                'analytics',
                'advertisement',
            ],
        }
    ];

    static #SCRIPT_ID = 'cookieyes';

    static #COOKIE_NAME = 'cookieyes-consent';

    static #EVENT_CONSENT_UPDATE = 'cookieyes_consent_update';

    static #CLASS_NAME_BLOCKED_CONTENT = 'blocked-content';
    static #CLASS_NAME_REVIEW_CONSENT = `${this.#CLASS_NAME_BLOCKED_CONTENT}__btn`;
    static #DATASET_BLOCKED_CONTENT = 'blockedContent';

    static get enabled() {
        return document.getElementById(this.#SCRIPT_ID) !== null;
    }

    static #getCookieConsentValue(): Record<string, string> {
        const cookie: string | undefined = document.cookie.split(/;\s*/).reduce((cookies: Record<string, string>, cookie) => {
            const [name, value] = cookie.split('=');

            cookies[name] = value;

            return cookies;
        }, {})[this.#COOKIE_NAME];

        if (!cookie) {
            return {};
        }

        return cookie.split(',').reduce((consents: Record<string, string>, consent) => {
            const [key, value] = consent.split(':');

            consents[key] = value;

            return consents;
        }, {});
    }

    static isConsentGiven(key: string | string[]): boolean {
        if (!this.enabled) {
            return true;
        }

        const consent = this.#getCookieConsentValue();

        if (typeof key === 'string') {
            return consent[key] === 'yes';
        }

        return key.every((key) => consent[key] === 'yes');
    }

    static isProviderAllowed(name: string): boolean {
        return this.isConsentGiven(this.getProviderCategories(name));
    }

    static getProviderCategories(name: string): string[] {
        return this.#providers.find((provider) => provider.name === name).categories;
    }

    static onConsentUpdate(callback: (consents: Record<string, string[]>) => void): () => void {
        const fn = (event: CustomEvent) => {
            callback(event.detail);
        };

        EventHandler.on(document, this.#EVENT_CONSENT_UPDATE, fn);

        return () => {
            EventHandler.off(document, this.#EVENT_CONSENT_UPDATE, fn);
        };
    }

    static #isConsentAccepted(key: string | string[], accepted: string[]): boolean {
        if (typeof key === 'string') {
            return accepted.includes(key);
        }

        return key.every((key) => accepted.includes(key));
    }

    static onceConsentGiven(key: string | string[], callback: () => void): void {
        if (this.isConsentGiven(key)) {
            return callback();
        }

        const removeConsentUpdateListener = this.onConsentUpdate((consents) => {
            if (this.#isConsentAccepted(key, consents.accepted)) {
                removeConsentUpdateListener();

                callback();
            }
        });
    }

    static onceProviderAllowed(name: string, callback: () => void): void {
        if (this.isProviderAllowed(name)) {
            return callback();
        }

        const removeConsentUpdateListener = this.onConsentUpdate((consents) => {
            if (this.#isConsentAccepted(this.getProviderCategories(name), consents.accepted)) {
                removeConsentUpdateListener();

                callback();
            }
        });
    }

    static #blockContent(element: HTMLElement): HTMLElement {
        const block = document.createElement('div');
        block.classList.add(this.#CLASS_NAME_BLOCKED_CONTENT);

        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add(this.#CLASS_NAME_REVIEW_CONSENT);
        button.textContent = element.dataset[this.#DATASET_BLOCKED_CONTENT] ?? window.App.lang.blockedContent;

        block.append(button);

        element.append(block);

        EventHandler.on(button, EVENT_CLICK, () => {
            window.revisitCkyConsent();
        });

        return block;
    }

    static blockContentByConsent(element: HTMLElement, key: string | string[]): void {
        const block = this.#blockContent(element);

        this.onceConsentGiven(key, () => {
            block.remove();
        });
    }

    static blockContentByProvider(element: HTMLElement, name: string): void {
        const block = this.#blockContent(element);

        this.onceProviderAllowed(name, () => {
            block.remove();
        });
    }
}
